import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, StatusBadge } from "@/components/bits";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg, fmtDate } from "@/lib/api";

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "hiring_manager", password: "" });
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/employer/team").then(({ data }) => setMembers(data.members)).catch(() => setMembers([]));
  useEffect(() => {
    load();
  }, []);

  const invite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/employer/team", { ...form, password: form.password || undefined });
      setCreated(data);
      setForm({ name: "", email: "", role: "hiring_manager", password: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.post(`/employer/team/${id}/status`, { status });
      toast.success(`Member ${status}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const isOwner = !user?.member_id && ["employer_owner", "super_admin", "admin"].includes(user?.role);

  return (
    <div data-testid="team-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <p className="text-sm text-muted-foreground">
          Invite hiring managers and interviewers — they work in your company's workspace with their own login.
        </p>
        {isOwner && (
          <Button onClick={() => { setOpen(true); setCreated(null); }} className="rounded-full bg-[#0B1B3D]" data-testid="invite-member-btn">
            <UserPlus className="w-4 h-4 mr-1.5" /> Invite Member
          </Button>
        )}
      </div>

      {!members ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : members.length === 0 ? (
        <EmptyState title="No team members yet" description="Invite colleagues to collaborate on hiring — they'll get their own login with scoped access." testId="team-empty" />
      ) : (
        <div className="bg-white border border-border/60 rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="team-table">
            <thead className="bg-[#F8FAFC] border-b border-border/60 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-5 py-3.5 font-semibold">Member</th>
                <th className="px-5 py-3.5 font-semibold">Role</th>
                <th className="px-5 py-3.5 font-semibold">Status</th>
                <th className="px-5 py-3.5 font-semibold">Added</th>
                {isOwner && <th className="px-5 py-3.5 font-semibold text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-b border-border/40" data-testid={`member-${m.id}`}>
                  <td className="px-5 py-4">
                    <p className="font-semibold text-[#0B1B3D]">{m.name}</p>
                    <p className="text-xs text-muted-foreground">{m.email}</p>
                  </td>
                  <td className="px-5 py-4 text-xs capitalize">{(m.role || "").replace(/_/g, " ")}</td>
                  <td className="px-5 py-4"><StatusBadge status={m.status} /></td>
                  <td className="px-5 py-4 text-xs text-muted-foreground">{fmtDate(m.created_at)}</td>
                  {isOwner && (
                    <td className="px-5 py-4 text-right">
                      {m.status === "active" ? (
                        <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => setStatus(m.id, "suspended")} data-testid={`suspend-member-${m.id}`}>Suspend</Button>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-full text-emerald-700 border-emerald-200" onClick={() => setStatus(m.id, "active")} data-testid={`activate-member-${m.id}`}>Reactivate</Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="invite-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{created ? "Member invited" : "Invite team member"}</DialogTitle></DialogHeader>
          {created ? (
            <div className="space-y-4" data-testid="invite-success">
              <p className="text-sm text-muted-foreground">Share these credentials with {created.name}:</p>
              <div className="bg-[#F8FAFC] border border-border rounded-lg p-4 text-sm font-mono">
                <p>Email: {created.email}</p>
                <p>Password: {created.temporary_password}</p>
              </div>
              <p className="text-xs text-muted-foreground">They can change the password after first login.</p>
              <Button className="w-full rounded-full bg-[#0B1B3D]" onClick={() => setOpen(false)} data-testid="invite-done-btn">Done</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="invite-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="invite-email" /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employer_admin">Employer Admin</SelectItem>
                    <SelectItem value="hiring_manager">Hiring Manager</SelectItem>
                    <SelectItem value="interviewer">Interviewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Password (optional — auto-generated if blank)</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="invite-password" /></div>
              <Button onClick={invite} disabled={busy || !form.name || !form.email} className="w-full rounded-full bg-[#0B1B3D]" data-testid="invite-submit-btn">
                {busy ? "Inviting…" : "Send Invite"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
